import React, {
	useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState,
} from 'react';
import {
	MaterialReactTable, MRT_ShowHideColumnsButton, MRT_ToggleDensePaddingButton, MRT_ToggleFiltersButton,
	MRT_ToggleFullScreenButton, MRT_ToggleGlobalFilterButton
} from 'material-react-table';
import {
	SmartDivider,
	SmartTableContainerSx, SmartTaskCount,
	SmartToolbarContainerFull, SmartToolbarGroupCenter,
	SmartToolbarGroupLeft, SmartToolbarGroupRight
} from '~/FielderElements/SmartTaskTable/SmartStyles';
import { useLanguage } from 'Context/LanguageContext';
import { getUserLanguage } from '~/FielderElements/SmartTaskTable/SmartTableUtils';
import { Box, Tooltip } from '@mui/material';
import Button from '@mui/material/Button';
import { AccountTree } from '@mui/icons-material';
import { FIELDER } from '@core/constants/base.constants';

const SmartFielderTable = React.forwardRef((
	{
		rows = [],
		columns = [],
		loadMore,
		onRowClick,
		height = 400,
		tableCookie,
		enablePagination,
		enableRowSelection,
		enableToolbarInternalActions,
		isLoading = false,
		rowCount = null,
		...rest
	}, ref) => {
	const lan = useLanguage();
	const userLanguage = getUserLanguage(lan, FIELDER.smartTableId.AUTO_ASSIGN, null);
	const data = useMemo(() => [...rows], [rows]);
	const [rowSelection, setRowSelection] = useState({});
	const [columnOrder, setColumnOrder] = useState(() => columns.map((c) => c.accessorKey || c.id));
	const tableContainerRef = useRef(null);
	const selectedRowsRef = useRef([]);
	const selectedIndexesRef = useRef([]);
	useImperativeHandle(ref, () => ({
		getState: () => ({
			selectedRows: selectedRowsRef.current,
			selectedIndexes: selectedIndexesRef.current,
		}),
		resetSelection: () => {
			selectedRowsRef.current = [];
			selectedIndexesRef.current = [];
			setRowSelection({});
		},
	}), []);
	useEffect(() => {
		setColumnOrder(columns.map((c) => c.accessorKey || c.id));
	}, [columns]);
	const handleScroll = useCallback(() => {
		const node = tableContainerRef.current;
		if (!node || !loadMore) return;
		const { scrollTop, scrollHeight, clientHeight } = node;
		if (scrollHeight - scrollTop - clientHeight < 50) loadMore();
	}, [loadMore]);
	useEffect(() => {
		const node = tableContainerRef.current;
		if (!node) return;
		node.addEventListener('scroll', handleScroll);
		return () => node.removeEventListener('scroll', handleScroll);
	}, [handleScroll]);
	return (
		<MaterialReactTable
			columns={columns}
			data={data}
			enableColumnOrdering
			enableRowVirtualization
			enablePagination={false}
			rowVirtualizerProps={{ overscan: 3 }}
			localization={userLanguage}
			enableRowSelection={enableRowSelection}
			enableToolbarInternalActions={enableToolbarInternalActions}
			muiTableContainerProps={{
				ref: tableContainerRef,
				sx: SmartTableContainerSx(height),
			}}
			initialState={{ density: 'compact' }}
			state={{ rowSelection, columnOrder, isLoading }}
			onColumnOrderChange={setColumnOrder}
			onRowSelectionChange={(updater) => {
				const next = typeof updater === 'function' ? updater(rowSelection) : updater;
				const indexes = Object.keys(next).map((id) => parseInt(id, 10));
				selectedIndexesRef.current = indexes;
				selectedRowsRef.current = indexes.map((i) => data[i]);
				setRowSelection(next);
			}}
			muiTableBodyRowProps={({ row }) => ({
				onClick: (event) => {
					const columnElement = event.target.closest('[data-column-id]');
					const columnId = columnElement?.getAttribute('data-column-id');
					console.log('priority value:', row.original.priority);
					onRowClick?.(row.index, row.original, { id: columnId });
				},
				sx: { cursor: 'pointer' },
			})}
			positionGlobalFilter={"left"}
			renderTopToolbarCustomActions={({ table }) => {
				return (
					<Box sx={SmartToolbarContainerFull}>
						<Box sx={SmartToolbarGroupLeft}></Box>
						<Box sx={SmartToolbarGroupCenter}></Box>
						<Box sx={SmartToolbarGroupRight}>
							<MRT_ToggleGlobalFilterButton table={table} />
							<MRT_ToggleFiltersButton table={table} />
							<MRT_ShowHideColumnsButton table={table} />
							<MRT_ToggleDensePaddingButton table={table} />
							<MRT_ToggleFullScreenButton table={table} />
							<SmartDivider orientation="vertical" variant="middle" flexItem />
							<Tooltip arrow title={lan.autoAssignTasks}>
								<Button
									size="large"
									startIcon={<AccountTree />}
									loading={isLoading}
									loadingPosition="start"
									disabled={isLoading}
									style={{color: '#dc5c87'}}>
									<SmartTaskCount variant={'h5'}>{rowCount}</SmartTaskCount>
								</Button>
							</Tooltip>
						</Box>
					</Box>
				);
			}}
			{...rest}
		/>
	);
});
export default SmartFielderTable;
